/**
 * Deduplication and rate limiting for error capture.
 *
 * A single broken render can fire the same error dozens of times per second
 * (React re-renders, retry loops, event storms). Without a gate we would hammer
 * the feedback endpoint and bury the signal. Two checks run before any report
 * leaves the device:
 *
 *  - **Dedupe**: the same fingerprint is dropped if seen within DEDUPE_WINDOW.
 *  - **Rate limit**: at most MAX_PER_WINDOW reports leave per RATE_WINDOW,
 *    regardless of fingerprint.
 */

const DEDUPE_WINDOW_MS = 10_000;
const RATE_WINDOW_MS = 60_000;
const MAX_PER_WINDOW = 10;

const lastSeen = new Map<string, number>();
let recentSends: number[] = [];

/** Stable identity for an error: source + name + first stack frame. */
export function fingerprint(input: {
  source: string;
  errorName: string;
  errorMessage: string;
  stack?: string;
}): string {
  const firstFrame = input.stack?.split("\n").find((l) => l.includes("at "));
  const locus = firstFrame?.trim() || input.errorMessage;
  return `${input.source}|${input.errorName}|${locus}`;
}

/**
 * Returns true if a report with this fingerprint should be sent now, and
 * records the decision. Returns false when deduped or rate limited.
 */
export function shouldReport(fp: string, now: number = Date.now()): boolean {
  // Evict fingerprints older than the dedupe window: they can no longer
  // suppress anything, so retaining them would grow lastSeen unbounded over a
  // long-lived session with many distinct errors.
  for (const [key, seen] of lastSeen) {
    if (now - seen >= DEDUPE_WINDOW_MS) {
      lastSeen.delete(key);
    }
  }

  const previous = lastSeen.get(fp);
  if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) {
    return false;
  }

  recentSends = recentSends.filter((t) => now - t < RATE_WINDOW_MS);
  if (recentSends.length >= MAX_PER_WINDOW) {
    return false;
  }

  lastSeen.set(fp, now);
  recentSends.push(now);
  return true;
}

/** Test helper — clears dedupe and rate-limit bookkeeping. */
export function resetDedupeState(): void {
  lastSeen.clear();
  recentSends = [];
}

/** Test helper — number of fingerprints currently retained. */
export function trackedFingerprintCount(): number {
  return lastSeen.size;
}
